import React, { useState, useEffect, useMemo, useRef } from 'react';
import Snackbar from './Snackbar';
import { useAutoScroll } from '../hooks/useAutoScroll';
import { getApiUrl } from '../config/api';
import { maskChassisNumber } from '../utils/maskUtils';
import { grossFromNet } from '../utils/productRatesApi';
import { inclusiveEndForCoverPeriod } from '../utils/calendarDates';
import {
  validateKenyanVehicleRegistration,
  isRegistrationCompleteForNtsaLookup,
  isTukTukVehicleClass,
  normalizeKenyanRegistration
} from '../utils/kenyanRegistrationValidation';
import { getInstitutionalPllFee, institutionalPllLabel } from '../utils/institutionalPll';
import {
  COMP_GLOBAL_MIN_SUM_INSURED,
  MAX_VEHICLE_AGE_YEARS
} from '../utils/comprehensiveRates';
import { formatKesAmountInput, parseKesAmount } from '../utils/kesAmountFormat';
import ComprehensiveBenefitsSection, {
  useLiveComprehensiveQuote,
  isMotorcycleCompClass
} from './ComprehensiveBenefitsSection';

const FIXED_TONNAGE = '1';
const TONNAGE_FIXED_CLASSES = new Set(['Tuk Tuk Commercial']);

// Normalize to YYYY-MM-DD for API and comparisons (Cover Details and backend use this format)
const toYYYYMMDD = (val) => {
  if (!val) return '';
  const s = String(val).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().split('T')[0];
};

// Normalize 2-digit/4-digit year values from DMVIC vehicle search to YYYY.
const normalizeYearOfManufacture = (rawYear) => {
  const cleaned = String(rawYear ?? '').trim();
  if (!cleaned) return '';

  let year = parseInt(cleaned, 10);
  if (Number.isNaN(year)) return '';

  const currentYear = new Date().getFullYear();
  const maxYear = currentYear + 1;
  const minYear = 1900;

  if (year >= 0 && year <= 99) {
    const cutoff = maxYear % 100;
    year = year <= cutoff ? (2000 + year) : (1900 + year);
  }

  if (year < minYear) year = minYear;
  if (year > maxYear) year = maxYear;
  return String(year);
};

const VehicleDetails = ({ coverType, selectedClass, institutionType = null, startDate, endDate, coverPeriod, pricing = [], initialData, onComplete, onBack, onBackToCoverDetails, onSaveAndGoToCoverDetails, extensionAnnualEndDate = null }) => {
  const coverStartRef = useRef(startDate);
  const coverEndRef = useRef(endDate);
  const vehicleSearchAbortRef = useRef(null);
  const registrationOnScreenRef = useRef(initialData?.registrationNumber || '');
  const lastSuccessfulLookupRef = useRef('');
  // Function to calculate PSV pricing based on passenger capacity
  // PSV Chauffer Driven: base + (passengers × 500) – both base and PLL are gross amounts
  const calculatePSVPricing = (baseAmount, passengerCount) => {
    const n = parseInt(passengerCount, 10) || 0;
    if (n <= 0) return baseAmount;
    return baseAmount + (n * 500);
  };

  // Function to format dates for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Return original if invalid
      
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return dateString; // Return original if formatting fails
    }
  };
  const [vehicleData, setVehicleData] = useState({
    registrationNumber: initialData?.registrationNumber || '',
    make: initialData?.make || '',
    model: initialData?.model || '',
    yearOfManufacture: initialData?.yearOfManufacture || '',
    chassisNumber: initialData?.chassisNumber || '',
    bodyType: initialData?.bodyType || '',
    engineNumber: initialData?.engineNumber || '',
    licensedToCarry: initialData?.licensedToCarry || '',
    tonnage: initialData?.tonnage || '',
    vehicleValue: initialData?.vehicleValue
      ? formatKesAmountInput(initialData.vehicleValue)
      : initialData?.sumInsured
        ? formatKesAmountInput(initialData.sumInsured)
        : ''
  });

  const [compBenefits, setCompBenefits] = useState(() => ({
    excessProtector: !!initialData?.comprehensiveBenefits?.excessProtector,
    pvt: !!initialData?.comprehensiveBenefits?.pvt,
    windscreen: Number(initialData?.comprehensiveBenefits?.windscreen) || 0,
    radio: Number(initialData?.comprehensiveBenefits?.radio) || 0,
    windscreenDisplay: initialData?.comprehensiveBenefits?.windscreen
      ? formatKesAmountInput(initialData.comprehensiveBenefits.windscreen)
      : '',
    radioDisplay: initialData?.comprehensiveBenefits?.radio
      ? formatKesAmountInput(initialData.comprehensiveBenefits.radio)
      : ''
  }));

  const isCompCover = String(coverType || '').toUpperCase() === 'COMP';
  const { quote: compQuote, error: compQuoteError } = useLiveComprehensiveQuote({
    enabled: isCompCover,
    vehicleClass: selectedClass,
    sumInsuredRaw: vehicleData.vehicleValue,
    yearOfManufacture: vehicleData.yearOfManufacture,
    benefits: compBenefits
  });

  useEffect(() => {
    if (isCompCover && isMotorcycleCompClass(selectedClass)) {
      setCompBenefits((b) => ({
        ...b,
        excessProtector: false,
        pvt: false,
        windscreen: 0,
        radio: 0,
        windscreenDisplay: '',
        radioDisplay: ''
      }));
    }
  }, [isCompCover, selectedClass]);

  const buildVehiclePayload = () => {
    const si = parseKesAmount(vehicleData.vehicleValue);
    const payload = {
      ...vehicleData,
      vehicleValue: Number.isFinite(si) ? si : vehicleData.vehicleValue,
      sumInsured: Number.isFinite(si) ? si : undefined
    };
    if (isCompCover) {
      payload.comprehensiveBenefits = {
        excessProtector: !!compBenefits.excessProtector,
        pvt: !!compBenefits.pvt,
        windscreen: Number(compBenefits.windscreen) || 0,
        radio: Number(compBenefits.radio) || 0
      };
      payload.comprehensiveQuote = compQuote || null;
    }
    return payload;
  };
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState('info');
  const [isCheckingInsurance, setIsCheckingInsurance] = useState(false);
  const [insuranceCheckComplete, setInsuranceCheckComplete] = useState(false);
  const [doubleInsuranceCheck, setDoubleInsuranceCheck] = useState({
    isChecking: false,
    hasDoubleInsurance: false,
    canProceed: false,
    existingPolicies: [],
    nextAvailableDate: null,
    error: null
  });

  const [vehicleSearch, setVehicleSearch] = useState({
    isSearching: false,
    found: false,
    error: null,
    errorStatus: null,  // e.g. 502 for retry prompt
    progressMessage: null
  });

  const VEHICLE_SEARCH_PROGRESS_MESSAGES = useMemo(() => ([
    'Looking up vehicle details…',
    'Checking registration records…',
    'Confirming make, model and chassis…',
    'Almost there — finalizing your vehicle information…'
  ]), []);

  // Rotate calm progress copy while search runs (covers longer lookups without alarming the user)
  useEffect(() => {
    if (!vehicleSearch.isSearching) return undefined;
    let index = 0;
    setVehicleSearch(prev => ({
      ...prev,
      progressMessage: VEHICLE_SEARCH_PROGRESS_MESSAGES[0]
    }));
    const timer = setInterval(() => {
      index = (index + 1) % VEHICLE_SEARCH_PROGRESS_MESSAGES.length;
      setVehicleSearch(prev => {
        if (!prev.isSearching) return prev;
        return { ...prev, progressMessage: VEHICLE_SEARCH_PROGRESS_MESSAGES[index] };
      });
    }, 3500);
    return () => clearInterval(timer);
  }, [vehicleSearch.isSearching, VEHICLE_SEARCH_PROGRESS_MESSAGES]);

  // Re-entry validation state for chassis, engine, and year when vehicle not found
  const [reEntryValidation, setReEntryValidation] = useState({
    required: false,
    chassisNumber1: '',
    chassisNumber2: '',
    engineNumber1: '',
    engineNumber2: '',
    yearOfManufacture1: '',
    yearOfManufacture2: '',
    allMatch: false
  });

  // Track if registration number is empty (new vehicle)
  const [isNewVehicle, setIsNewVehicle] = useState(false);

  // Chassis from DMVIC: mask in UI (show first 4 chars + asterisks), full value always used for save/transmit
  const [chassisFromDmvic, setChassisFromDmvic] = useState(false);
  const [chassisInputFocused, setChassisInputFocused] = useState(false);

  // Track user's choice to use next available date
  const [useNextAvailableDate, setUseNextAvailableDate] = useState(false);
  
  // Track effective dates to use for double insurance check (updated when user confirms next available date)
  const [effectiveStartDate, setEffectiveStartDate] = useState(startDate);
  const [effectiveEndDate, setEffectiveEndDate] = useState(endDate);

  // PLL: only Motorcycle PSV is fixed (1 passenger, greyed). Motor Commercial Institutional: user inputs 1–72 (editable). PSV Chauffer: 3 or 4.
  const PLL_RESTRICTED = {
    'Motorcycle PSV': { value: 1, max: 1, label: '1 passenger (fixed)' },
    'Tuk Tuk PSV': { value: 3, max: 3, label: '3 passengers (fixed)' }
  };
  const isPllRestrictedClass = (cls) => cls && PLL_RESTRICTED[cls];
  const INSTITUTIONAL_PLL_FEE = getInstitutionalPllFee(institutionType || initialData?.institutionType);
  const INSTITUTIONAL_PLL_MAX = 72;

  // Prefill PLL when vehicle class is fixed (Motorcycle PSV) or PSV Chauffer (default 4). Institutional: no prefill, user enters 1–72.
  useEffect(() => {
    if (!selectedClass) return;
    const restricted = PLL_RESTRICTED[selectedClass];
    if (restricted) {
      setVehicleData(prev => ({ ...prev, licensedToCarry: String(restricted.value) }));
    } else if (selectedClass === 'PSV Chauffer Driven' && !vehicleData.licensedToCarry) {
      setVehicleData(prev => ({ ...prev, licensedToCarry: '4' }));
    }
    if (TONNAGE_FIXED_CLASSES.has(selectedClass)) {
      setVehicleData(prev => ({ ...prev, tonnage: FIXED_TONNAGE }));
    } else if (!getRequiredFieldsForClass(selectedClass, coverType).includes('tonnage')) {
      setVehicleData(prev => ({ ...prev, tonnage: '' }));
    }
  }, [selectedClass, coverType]);
  
  // Field validation function for useAutoScroll (memoized)
  const isFieldValid = useMemo(() => (fieldName, value) => {
    if (!value || value.toString().trim() === '') return false;
    
    switch (fieldName) {
      case 'registrationNumber': {
        if (isNewVehicle) return false;
        return validateKenyanVehicleRegistration(value, selectedClass, {
          chassisNumber: vehicleData?.chassisNumber,
          make: vehicleData?.make,
          model: vehicleData?.model,
          bodyType: vehicleData?.bodyType,
          isNewVehicle
        }).valid;
      }
      case 'make':
        // Require at least 3 characters for vehicle make
        return value.length >= 3;
      case 'yearOfManufacture':
        const year = parseInt(value);
        const currentYear = new Date().getFullYear();
        return year >= 1900 && year <= (currentYear + 1) && value.length === 4;
      case 'chassisNumber':
        // Chassis numbers are typically longer and more specific
        return value.length >= 8;
      case 'bodyType':
        // Require at least 3 characters for body type
        return value.length >= 3;
      case 'engineNumber':
        // Engine numbers are typically longer
        return value.length >= 5;
      case 'licensedToCarry':
        const carryValue = parseInt(value, 10);
        if (!carryValue || carryValue < 1) return false;
        if (selectedClass === 'Motorcycle PSV') return carryValue === 1;
        if (selectedClass === 'Tuk Tuk PSV') return carryValue === 3;
        if (selectedClass === 'PSV Chauffer Driven') return carryValue === 3 || carryValue === 4;
        if (selectedClass === 'Motor Commercial Institutional') return carryValue <= 72;
        return carryValue <= 50;
      case 'tonnage':
        const tonnageValue = parseInt(value, 10);
        if (TONNAGE_FIXED_CLASSES.has(selectedClass)) return tonnageValue === 1;
        return Number.isInteger(tonnageValue) && tonnageValue >= 1 && tonnageValue <= 999;
      case 'vehicleValue': {
        const val = parseKesAmount(value);
        if (isCompCover) {
          return Number.isFinite(val) && val >= COMP_GLOBAL_MIN_SUM_INSURED;
        }
        return Number.isFinite(val) && val > 0;
      }
      default:
        return false;
    }
  }, [selectedClass, isNewVehicle, vehicleData?.chassisNumber, vehicleData?.make, vehicleData?.model, vehicleData?.bodyType, isCompCover]);

  // Helper functions for conditional field logic. Tonnage is required for all Motor Commercial (TPO: tonnage-based premium; TOR: flat 600, tonnage for record only). Only TOR Motor Private has no tonnage (flat 600).
  const getRequiredFieldsForClass = (selectedClass, coverType) => {
    if (coverType === 'TOR' && selectedClass === 'Motor Private') {
      return ['bodyType'];
    }
    const fieldMap = {
      'PSV Chauffer Driven': ['bodyType', 'licensedToCarry'],
      'Motorcycle PSV': ['bodyType', 'licensedToCarry'],
      'Tuk Tuk PSV': ['bodyType', 'licensedToCarry'],
      'Motor Commercial Institutional': ['bodyType', 'tonnage', 'licensedToCarry'],
      'Motor Commercial General Cartage': ['bodyType', 'tonnage'],
      'Motor Commercial Own Goods': ['bodyType', 'tonnage'],
      'Tractor TPO': ['bodyType', 'tonnage'],
      'Tuk Tuk Commercial': ['bodyType', 'tonnage'],
      'Motor Commercial': ['bodyType', 'tonnage'],
      'Prime Movers': ['bodyType'],
      'Motor Private': ['bodyType'],
      'Motorcycle Private': ['bodyType']
    };
    return fieldMap[selectedClass] || ['bodyType'];
  };

  // Dynamic field order. Tonnage shown for all Motor Commercial (TPO = affects premium; TOR = flat 600, tonnage for record only); hidden only for TOR Motor Private.
  const getFieldOrder = (selectedClass, coverType) => {
    const baseOrder = [
      'registrationNumber',
      'make',
      'yearOfManufacture',
      'chassisNumber',
      'bodyType',
      'engineNumber',
      'vehicleValue'
    ];
    const conditionalFields = getRequiredFieldsForClass(selectedClass, coverType);
    if (conditionalFields.includes('licensedToCarry')) {
      baseOrder.push('licensedToCarry');
    }
    if (conditionalFields.includes('tonnage')) {
      baseOrder.push('tonnage');
    }
    return baseOrder;
  };

  const fieldOrder = useMemo(() => getFieldOrder(selectedClass, coverType), [selectedClass, coverType]);
  const isTractorTPO = selectedClass === 'Tractor TPO';
  const isTukTukCommercial = selectedClass === 'Tuk Tuk Commercial';
  const isTukTukClass = isTukTukVehicleClass(selectedClass);

  const registrationValidationOptions = (overrides = {}) => ({
    chassisNumber: vehicleData.chassisNumber,
    make: vehicleData.make,
    model: vehicleData.model,
    bodyType: vehicleData.bodyType,
    isNewVehicle,
    ...overrides
  });

  // Use the auto-scroll hook
  const { 
    initializeFieldStates, 
    updateFieldValidationState,
    handleFocus,
    handleBlur,
    scrollToTop
  } = useAutoScroll(fieldOrder, isFieldValid);

  // Debounced input change handler
  const handleInputChange = (name, value) => {
    let processedValue = value;

    if (name === 'vehicleValue') {
      const digits = String(value || '').replace(/[^\d]/g, '');
      processedValue = digits ? formatKesAmountInput(digits) : '';
    }

    // Special handling for registration number
    if (name === 'registrationNumber') {
      // Remove spaces and special characters, keep only alphanumeric
      processedValue = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
      
      // Check if registration is empty (new vehicle)
      setIsNewVehicle(processedValue.trim() === '');
      
      // Clear search status when user starts typing. Do not look the plate up until they leave the field.
      if (processedValue !== vehicleData.registrationNumber) {
        registrationOnScreenRef.current = processedValue;
        lastSuccessfulLookupRef.current = '';
        if (vehicleSearchAbortRef.current) {
          vehicleSearchAbortRef.current.abort();
          vehicleSearchAbortRef.current = null;
        }
        setVehicleSearch({
          isSearching: false,
          found: false,
          error: null,
          progressMessage: null
        });
        
        // Clear double insurance check when user changes registration number
        setDoubleInsuranceCheck({
          isChecking: false,
          hasDoubleInsurance: false,
          existingPolicies: [],
          nextAvailableDate: null,
          error: null
        });
        
        // Clear snackbar when user changes registration number
        setShowSnackbar(false);
      }
    }

    // Special handling for chassis number
    if (name === 'chassisNumber') {
      // DMVIC: alphanumeric only (no hyphens/spaces). Logbooks often include dashes.
      processedValue = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
      // User edited chassis so it's no longer "from DMVIC" for display masking
      setChassisFromDmvic(false);
      
      // Clear double insurance check when user changes chassis number
      if (processedValue !== vehicleData.chassisNumber) {
        setDoubleInsuranceCheck({
          isChecking: false,
          hasDoubleInsurance: false,
          existingPolicies: [],
          error: null
        });
        
        // Clear snackbar when user changes chassis number
        setShowSnackbar(false);
      }
    }

    // Special handling for year of manufacture (strict 4-digit year)
    if (name === 'yearOfManufacture') {
      processedValue = value.replace(/\D/g, '').slice(0, 4);
    }

    // Tonnage must be whole-number tonnes (DMVIC rejects decimal values).
    if (name === 'tonnage') {
      if (TONNAGE_FIXED_CLASSES.has(selectedClass)) {
        processedValue = FIXED_TONNAGE;
      } else {
        processedValue = value.replace(/\D/g, '').slice(0, 3);
      }
    }

    // Update the field value immediately for UI responsiveness
    setVehicleData(prev => ({
      ...prev,
      [name]: processedValue
    }));

    // Call updateFieldValidationState immediately to track typing activity
    // The hook will handle intelligent debouncing internally
    updateFieldValidationState(name, processedValue);
  };
  // Keep refs and effective dates in sync with Cover Details (single source of truth from parent)
  useEffect(() => {
    const start = toYYYYMMDD(startDate);
    const end = toYYYYMMDD(endDate);
    coverStartRef.current = start || startDate;
    coverEndRef.current = end || endDate;
    setEffectiveStartDate(startDate);
    setEffectiveEndDate(endDate);
  }, [startDate, endDate]);

  // When Cover Details dates change (e.g. user went back and chose 8th), clear stale double insurance result so next check uses the new date
  const prevDatesRef = useRef({ startDate: null, endDate: null });
  useEffect(() => {
    const start = toYYYYMMDD(startDate);
    const end = toYYYYMMDD(endDate);
    if (prevDatesRef.current.startDate !== start || prevDatesRef.current.endDate !== end) {
      prevDatesRef.current = { startDate: start, endDate: end };
      setDoubleInsuranceCheck(prev =>
        (prev.hasDoubleInsurance || prev.existingPolicies?.length) ? { isChecking: false, hasDoubleInsurance: false, canProceed: false, existingPolicies: [], nextAvailableDate: null, error: null } : prev
      );
    }
  }, [startDate, endDate]);

  // Initialize field states and scroll to first input field on component mount
  useEffect(() => {
    initializeFieldStates(fieldOrder);
    
    // Scroll to registration number field (first input) instead of just top
    setTimeout(() => {
      const registrationField = document.getElementById('registrationNumber');
      if (registrationField) {
        registrationField.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'nearest'
        });
        // Focus the field after a short delay
        setTimeout(() => {
          registrationField.focus();
        }, 300);
      } else {
        // Fallback to top if field not found
        scrollToTop();
      }
    }, 100);
    
    if (initialData?.registrationNumber) {
      // If we have previous data, reset the check status but don't auto-run
      setInsuranceCheckComplete(false);
      setIsCheckingInsurance(false);
    }
  }, [initializeFieldStates, scrollToTop, fieldOrder, initialData?.registrationNumber]);

  useEffect(() => {
    return () => {
      const pending = vehicleSearchAbortRef.current;
      vehicleSearchAbortRef.current = null;
      if (pending) pending.abort();
    };
  }, []);

  const showNotification = (message, type = 'info', duration = 5000) => {
    setSnackbarMessage(message);
    setSnackbarType(type);
    setShowSnackbar(true);
  };

  /**
   * Production gate before checkout: align with getRequiredFieldsForClass + DMVIC Type B.
   * Tonnage for all commercial Type B lines; licensed capacity for PSV / institutional as on the form.
   */
  const validateDmvicFieldsBeforePayment = () => {
    const errors = [];
    const req = getRequiredFieldsForClass(selectedClass, coverType);
    const regClean = String(vehicleData.registrationNumber || '')
      .replace(/[^a-zA-Z0-9]/g, '')
      .toUpperCase();

    const plateCheck = validateKenyanVehicleRegistration(regClean, selectedClass, {
      ...registrationValidationOptions(),
      chassisNumber: vehicleData.chassisNumber
    });
    if (!isNewVehicle && !plateCheck.valid) {
      errors.push(plateCheck.message);
    }

    if (req.includes('tonnage')) {
      const ton = parseInt(String(vehicleData.tonnage || '').trim(), 10);
      if (TONNAGE_FIXED_CLASSES.has(selectedClass) && ton !== 1) {
        errors.push(
          `Tonnage is fixed at 1 tonne for ${selectedClass}.`
        );
      } else if (!TONNAGE_FIXED_CLASSES.has(selectedClass) && (!Number.isInteger(ton) || ton < 1 || ton > 999)) {
        errors.push(
          `Tonnage (carrying capacity in whole tonnes) must be an integer between 1 and 999. This class (${selectedClass}) requires it for DMVIC Type B — complete the field before payment.`
        );
      }
    }

    if (req.includes('licensedToCarry')) {
      const ltc = parseInt(String(vehicleData.licensedToCarry || '').trim(), 10);
      if (!Number.isFinite(ltc) || ltc < 1) {
        errors.push('Enter licensed passenger capacity before continuing.');
      } else if (selectedClass === 'PSV Chauffer Driven' && ltc !== 3 && ltc !== 4) {
        errors.push('PSV Chauffer Driven: passenger capacity must be 3 or 4.');
      } else if (selectedClass === 'Motorcycle PSV' && ltc !== 1) {
        errors.push('Motorcycle PSV: passenger capacity must be 1.');
      } else if (selectedClass === 'Tuk Tuk PSV' && ltc !== 3) {
        errors.push('Tuk Tuk PSV: passenger capacity must be 3.');
      } else if (selectedClass === 'Motor Commercial Institutional' && (ltc < 1 || ltc > 72)) {
        errors.push(
          'Motor Commercial Institutional: passenger capacity must be 1–72. NTSA often shows 0 — use the logbook.'
        );
      }
    }

    return { ok: errors.length === 0, errors };
  };

  const handleContinue = async () => {
    const dmvicGate = validateDmvicFieldsBeforePayment();
    if (!dmvicGate.ok) {
      showNotification(dmvicGate.errors.join(' '), 'error', 0);
      return;
    }
    if (!isFormComplete()) return;
    const nextAvail = doubleInsuranceCheck.nextAvailableDate;
    const nextAvailNorm = toYYYYMMDD(nextAvail) || nextAvail;
    const chosenStartDate =
      (useNextAvailableDate && nextAvailNorm) ||
      coverStartRef.current ||
      toYYYYMMDD(effectiveStartDate) ||
      toYYYYMMDD(startDate);
    const hasChosenLaterDate = nextAvailNorm && chosenStartDate && chosenStartDate >= nextAvailNorm;
    const canProceed = doubleInsuranceCheck.canProceed || (doubleInsuranceCheck.hasDoubleInsurance && hasChosenLaterDate);
    if (doubleInsuranceCheck.hasDoubleInsurance && canProceed) {
      const dateToUse = useNextAvailableDate ? (nextAvailNorm || nextAvail) : chosenStartDate;
      const insuranceResult = {
        hasActiveCover: false,
        message: 'Using start date on or after next available date',
        checkDate: new Date().toISOString(),
        dmvicResponse: null
      };
      onComplete(buildVehiclePayload(), insuranceResult, dateToUse || chosenStartDate);
      return;
    }
    await performRealInsuranceCheck();
  };

  // Helper function to calculate end date
  const calculateEndDate = (start, period) => {
    if (!start || !period) return '';
    // Second-half extend: never recompute from start + '1 Year (50%)' (extra month).
    const annualPin = extensionAnnualEndDate ? toYYYYMMDD(extensionAnnualEndDate) : '';
    if (coverType === 'TPO_EXTEND' && annualPin) return annualPin;
    if (coverType === 'TPO_EXTEND' && String(period) === '1 Year (50%)') {
      const existing = toYYYYMMDD(endDate) || toYYYYMMDD(coverEndRef.current);
      if (existing) return existing;
    }
    const p = period === '6 Month' ? '6 Months' : period;
    return inclusiveEndForCoverPeriod(toYYYYMMDD(start) || String(start).slice(0, 10), p);
  };

  const performRealInsuranceCheck = async () => {
    setIsCheckingInsurance(true);
    showNotification('Validating...', 'info', 3000);
    
    const fromCover =
      (useNextAvailableDate && toYYYYMMDD(doubleInsuranceCheck.nextAvailableDate)) ||
      coverStartRef.current ||
      toYYYYMMDD(effectiveStartDate) ||
      toYYYYMMDD(startDate);
    const userStartDate = fromCover || new Date().toISOString().split('T')[0];
    const endFromCover =
      (useNextAvailableDate && userStartDate && coverPeriod
        ? calculateEndDate(userStartDate, coverPeriod)
        : null) ||
      coverEndRef.current ||
      toYYYYMMDD(effectiveEndDate) ||
      toYYYYMMDD(endDate);
    const userEndDate =
      coverType === 'TPO_EXTEND'
        ? (endFromCover || '')
        : userStartDate && coverPeriod
          ? calculateEndDate(userStartDate, coverPeriod)
          : (endFromCover || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
    const startFinal = toYYYYMMDD(userStartDate) || userStartDate;
    const endFinal = toYYYYMMDD(userEndDate) || userEndDate;

    try {
      const response = await fetch(getApiUrl('/api/dmvic/validate-double-insurance'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          vehicleData: {
            chassisNumber: vehicleData.chassisNumber,
            registrationNumber: vehicleData.registrationNumber
          },
          vehicleClass: selectedClass,
          policyDates: {
            startDate: startFinal,
            endDate: endFinal
          }
        })
      });

      const result = await response.json();
      
      setIsCheckingInsurance(false);
      
      if (result.success) {
        if (result.hasDoubleInsurance) {
          const policies = result.existingPolicies || [];
          const canProceed = result.canProceed === true;
          setInsuranceCheckComplete(canProceed);
          setDoubleInsuranceCheck({
            isChecking: false,
            hasDoubleInsurance: true,
            canProceed,
            existingPolicies: policies,
            nextAvailableDate: result.nextAvailableDate || null,
            error: null
          });
          setUseNextAvailableDate(false);
          if (canProceed) {
            showNotification('✅ Your start date is valid. You may proceed to checkout.', 'success', 6000);
            const insuranceResult = {
              hasActiveCover: false,
              message: result.message,
              checkDate: new Date().toISOString(),
              dmvicResponse: result.response
            };
            const startFinal = toYYYYMMDD(coverStartRef.current || startDate || effectiveStartDate) || new Date().toISOString().split('T')[0];
            onComplete(buildVehiclePayload(), insuranceResult, startFinal);
            return;
          }
          showNotification(`❌ ${result.message}`, 'error', 8000);
          setSnackbarMessage(`Double Insurance Detected: ${result.message}`);
          setSnackbarType('error');
          setShowSnackbar(true);
          return; // Don't proceed to checkout when double insurance and cannot proceed
        } else {
          // No double insurance - proceed to checkout
          setInsuranceCheckComplete(true);
          showNotification('✅ Insurance check complete - No existing policy found', 'success', 4000);
          
          const insuranceResult = {
            hasActiveCover: false,
            message: result.message,
            checkDate: new Date().toISOString(),
            dmvicResponse: result.response
          };
          
          const dateToUse = useNextAvailableDate && doubleInsuranceCheck.nextAvailableDate
            ? doubleInsuranceCheck.nextAvailableDate
            : (coverStartRef.current || toYYYYMMDD(startDate) || effectiveStartDate || startDate);
          setTimeout(() => {
            onComplete(buildVehiclePayload(), insuranceResult, dateToUse);
          }, 2000);
        }
      } else {
        throw new Error(result.error || 'Insurance check failed');
      }
    } catch (error) {
      setIsCheckingInsurance(false);
      setInsuranceCheckComplete(false);
      showNotification(`❌ Insurance check failed: ${error.message}`, 'error', 8000);
      setSnackbarMessage(`Insurance check failed: ${error.message}`);
      setSnackbarType('error');
      setShowSnackbar(true);
    }
  };

  const performInsuranceCheck = async () => {
    setIsCheckingInsurance(true);
    showNotification('Validating...', 'info', 3000);
    
    // Simulate API call delay
    setTimeout(() => {
      setIsCheckingInsurance(false);
      setInsuranceCheckComplete(true);
      
      // For testing purposes: vehicle doesn't have active cover
      const insuranceResult = {
        hasActiveCover: false,
        message: 'No active insurance cover found for this vehicle',
        checkDate: new Date().toISOString()
      };
      
      showNotification('✅ Insurance check complete - No active cover found', 'success', 4000);
      
      // Complete the process - include next available date if user confirmed
      setTimeout(() => {
        onComplete(buildVehiclePayload(), insuranceResult, useNextAvailableDate ? doubleInsuranceCheck.nextAvailableDate : null);
      }, 2000);
    }, 3000);
  };

  // Look up a finished plate. Call this on blur or Validate, not while the agent is still typing.
  const searchVehicleDetails = async (registrationNumber, options = {}) => {
    const requested = normalizeKenyanRegistration(registrationNumber);
    if (!isRegistrationCompleteForNtsaLookup(requested, selectedClass)) {
      return;
    }
    if (!options.force && lastSuccessfulLookupRef.current === requested) {
      return;
    }
    registrationOnScreenRef.current = requested;

    if (vehicleSearchAbortRef.current) {
      vehicleSearchAbortRef.current.abort();
    }
    const controller = new AbortController();
    vehicleSearchAbortRef.current = controller;
    const timeoutId = setTimeout(() => controller.abort(), 120000);
    const stillCurrent = () =>
      vehicleSearchAbortRef.current === controller &&
      normalizeKenyanRegistration(registrationOnScreenRef.current) === requested;

    setVehicleSearch(prev => ({
      ...prev,
      isSearching: true,
      error: null,
      errorStatus: null,
      progressMessage: 'Looking up vehicle details…'
    }));

    try {
      const response = await fetch(getApiUrl('/api/dmvic/search-vehicle'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          registrationNumber: requested,
          vehicleClass: selectedClass
        }),
        signal: controller.signal
      });

      if (!stillCurrent()) return;

      // Handle server errors (502, 503, etc.) before parsing JSON – body may not be JSON
      if (!response.ok) {
        let errMsg = `Service error (${response.status})`;
        try {
          const text = await response.text();
          const data = text ? JSON.parse(text) : {};
          if (data.error) errMsg = data.error;
        } catch (_) {
          if (response.status === 502) errMsg = 'Service temporarily unavailable (502). Click Validate to try again.';
          else if (response.status === 503) errMsg = 'Service temporarily unavailable (503). Click Validate to try again.';
        }
        setVehicleSearch({
          isSearching: false,
          found: false,
          error: errMsg,
          errorStatus: response.status,
          progressMessage: null
        });
        setSnackbarMessage(`❌ Vehicle search failed: ${errMsg}`);
        setSnackbarType('error');
        setShowSnackbar(true);
        return;
      }

      const result = await response.json();
      if (!stillCurrent()) return;
      
      // Handle case where vehicle is found (has policy history) but vehicle details might be null/empty
      if (result.success && (result.vehicle || (result.policyHistory && result.policyHistory.length > 0))) {
        // Auto-fill vehicle details (vehicle object might be empty/null but vehicle exists in DB)
        const vehicle = result.vehicle || {};
        const returnedReg = normalizeKenyanRegistration(vehicle.VehicleRegistrationNumber || '');
        if (returnedReg && returnedReg !== requested) {
          setVehicleSearch({
            isSearching: false,
            found: false,
            error: `DMVIC returned ${returnedReg}, which is not the registration you entered (${requested}). Your registration was kept.`,
            errorStatus: null,
            progressMessage: null
          });
          showNotification(
            `DMVIC returned ${returnedReg}. The registration you entered (${requested}) was kept.`,
            'error',
            0
          );
          return;
        }

        lastSuccessfulLookupRef.current = requested;

        // Check if we actually have meaningful data to auto-fill
        const hasValidData = vehicle.VehicleRegistrationNumber || 
                           vehicle.ChassisNumber || 
                           vehicle.VehicleMake || 
                           vehicle.Model || 
                           vehicle.VehicleRegistrationYear || 
                           vehicle.BodyType;
        
        // Always update vehicle data if vehicle object exists (even if some fields are null)
        // This ensures registration number is preserved and any available fields are filled
        setVehicleData(prev => {
          const yearFromSearch = vehicle.VehicleRegistrationYear ?? vehicle.YearOfManufacture ?? vehicle.Year;
          const normalizedYearOfManufacture = normalizeYearOfManufacture(yearFromSearch);
          
          // Build updated vehicle data - preserve user input if vehicle field is null/empty
          const updatedData = {
            ...prev,
            // Keep the plate the agent typed. Never replace it with a shorter or different NTSA plate.
            registrationNumber: prev.registrationNumber,
            // Only update fields that have actual values
            chassisNumber: (vehicle.ChassisNumber && vehicle.ChassisNumber.trim()) 
              ? vehicle.ChassisNumber.trim() 
              : prev.chassisNumber,
            make: (vehicle.VehicleMake && vehicle.VehicleMake.trim()) 
              ? vehicle.VehicleMake.trim() 
              : prev.make,
            model: (vehicle.Model && vehicle.Model.trim()) 
              ? vehicle.Model.trim() 
              : (vehicle.VehicleModel && vehicle.VehicleModel.trim())
                ? vehicle.VehicleModel.trim()
                : prev.model,
            yearOfManufacture: normalizedYearOfManufacture && normalizedYearOfManufacture !== prev.yearOfManufacture
              ? normalizedYearOfManufacture
              : prev.yearOfManufacture,
            bodyType: (vehicle.BodyType && vehicle.BodyType !== null && vehicle.BodyType.trim()) 
              ? vehicle.BodyType.trim() 
              : prev.bodyType,
            carryingCapacity: (() => {
              const cap = vehicle.CarryingCapacity ?? vehicle.Tonnage ?? vehicle.tonnage;
              const capNum = cap != null && String(cap).trim() !== '' ? parseFloat(cap) : NaN;
              // Same 1–999 tonne guard as issuance; NTSA often returns kg (e.g. 1760).
              if (Number.isFinite(capNum) && capNum >= 1 && capNum <= 999) {
                return String(Math.trunc(capNum));
              }
              return prev.carryingCapacity;
            })(),
            engineNumber: (vehicle.EngineNumber && vehicle.EngineNumber.trim()) 
              ? vehicle.EngineNumber.trim() 
              : prev.engineNumber,
            tonnage: (() => {
              if (TONNAGE_FIXED_CLASSES.has(selectedClass)) return FIXED_TONNAGE;
              if (!getRequiredFieldsForClass(selectedClass, coverType).includes('tonnage')) {
                return prev.tonnage;
              }
              const prevTon = String(prev.tonnage || '').trim();
              const prevTonNum = parseFloat(prevTon);
              if (Number.isFinite(prevTonNum) && prevTonNum >= 1) return prev.tonnage;
              const cap = vehicle.CarryingCapacity ?? vehicle.Tonnage ?? vehicle.tonnage;
              const capNum = cap != null && String(cap).trim() !== '' ? parseFloat(cap) : NaN;
              if (Number.isFinite(capNum) && capNum >= 1 && capNum <= 999) return String(Math.trunc(capNum));
              return prev.tonnage;
            })(),
            licensedToCarry: (() => {
              if (selectedClass !== 'Motor Commercial Institutional') return prev.licensedToCarry;
              // Prefer agent-entered PLL; only use NTSA when the field is still empty.
              const prevNum = prev.licensedToCarry != null && String(prev.licensedToCarry).trim() !== ''
                ? parseInt(String(prev.licensedToCarry).trim(), 10)
                : NaN;
              if (!Number.isNaN(prevNum) && prevNum > 0) return String(prevNum);
              const ntsaRaw =
                vehicle.LicensedToCarry ??
                vehicle.licensedToCarry ??
                vehicle.Licensedtocarry ??
                vehicle.SeatingCapacity ??
                vehicle.passengerCapacity;
              const n = ntsaRaw != null && ntsaRaw !== '' ? parseInt(ntsaRaw, 10) : NaN;
              if (!Number.isNaN(n) && n > 0) return String(n);
              return prev.licensedToCarry;
            })()
          };

          return updatedData;
        });

        // Chassis populated from DMVIC: mask in UI (display only), full value kept for save/transmit
        if (vehicle.ChassisNumber && vehicle.ChassisNumber.trim()) {
          setChassisFromDmvic(true);
        }

        const resolvedMake = (vehicle.VehicleMake && vehicle.VehicleMake.trim()) || vehicleData.make;
        const resolvedModel =
          (vehicle.Model && vehicle.Model.trim()) ||
          (vehicle.VehicleModel && vehicle.VehicleModel.trim()) ||
          vehicleData.model;
        const resolvedBody =
          (vehicle.BodyType && vehicle.BodyType !== null && vehicle.BodyType.trim())
            ? vehicle.BodyType.trim()
            : vehicleData.bodyType;
        const resolvedReg = requested;
        const tukPlateCheck = validateKenyanVehicleRegistration(resolvedReg, selectedClass, {
          chassisNumber: (vehicle.ChassisNumber && vehicle.ChassisNumber.trim()) || vehicleData.chassisNumber,
          make: resolvedMake,
          model: resolvedModel,
          bodyType: resolvedBody,
          isNewVehicle
        });
        if (!tukPlateCheck.valid) {
          setVehicleSearch({
            isSearching: false,
            found: true,
            error: tukPlateCheck.message,
            progressMessage: null
          });
          showNotification(tukPlateCheck.message, 'error', 0);
          return;
        }

        setVehicleSearch({
          isSearching: false,
          found: true,
          error: null,
          progressMessage: null
        });

        // Determine message based on what data was found
        if (hasValidData) {
          setSnackbarMessage('✅ Vehicle details found and auto-filled');
          setSnackbarType('success');
        } else if (result.policyHistory && result.policyHistory.length > 0) {
          const activePolicies = result.policyHistory.filter(policy => {
            if (policy.CoverEndDate) {
              const endDate = new Date(policy.CoverEndDate.split('/').reverse().join('-'));
              return endDate >= new Date();
            }
            return false;
          });
          
          if (activePolicies.length > 0) {
            setSnackbarMessage(`✅ Vehicle found in DMVIC database (${activePolicies.length} active policy/policies detected)`);
            setSnackbarType('warning');
          } else {
            setSnackbarMessage('✅ Vehicle found in DMVIC database (has policy history)');
            setSnackbarType('info');
          }
        } else {
          setSnackbarMessage('✅ Vehicle found in DMVIC database (no details available)');
          setSnackbarType('info');
        }
        setShowSnackbar(true);
        
        // Clear search status after 5 seconds
        setTimeout(() => {
          setVehicleSearch(prev => ({ ...prev, found: false }));
        }, 5000);
      } else {
        setVehicleSearch({
          isSearching: false,
          found: false,
          error: result.error || 'Vehicle not found',
          errorStatus: null,
          progressMessage: null
        });

        // Enable re-entry validation when vehicle not found
        setReEntryValidation(prev => ({
          ...prev,
          required: true,
          chassisNumber1: vehicleData.chassisNumber || '',
          engineNumber1: vehicleData.engineNumber || '',
          yearOfManufacture1: vehicleData.yearOfManufacture || '',
          chassisNumber2: '',
          engineNumber2: '',
          yearOfManufacture2: '',
          allMatch: false
        }));

        setSnackbarMessage(`⚠️ ${result.error || 'Vehicle not found in DMVIC database'}. Please re-enter chassis, engine, and year for verification.`);
        setSnackbarType('warning');
        setShowSnackbar(true);
        
        // Clear search status after 5 seconds (Validate button stays until next search)
        setTimeout(() => {
          setVehicleSearch(prev => ({ ...prev, error: null }));
        }, 5000);
      }
    } catch (error) {
      if (!stillCurrent()) return;
      const status = error.response?.status;
      const isTimeout = error.name === 'TimeoutError' || error.name === 'AbortError' || (error.message && (error.message.includes('timeout') || error.message.includes('aborted')));
      const msg = status === 502
        ? 'Service temporarily unavailable (502). Click Validate to try again.'
        : status === 503
          ? 'Service temporarily unavailable (503). Click Validate to try again.'
          : isTimeout
            ? 'Vehicle search timed out. DMVIC may be slow. Please try again.'
            : error.message;
      setVehicleSearch({
        isSearching: false,
        found: false,
        error: msg,
        errorStatus: status ?? null,
        progressMessage: null
      });

      setSnackbarMessage(`❌ Vehicle search failed: ${msg}`);
      setSnackbarType('error');
      setShowSnackbar(true);
      
      // Don't auto-clear error when we offer Validate – user can retry
      setTimeout(() => {
        setVehicleSearch(prev => ({ ...prev, error: null }));
      }, 5000);
    } finally {
      clearTimeout(timeoutId);
      if (vehicleSearchAbortRef.current === controller) {
        vehicleSearchAbortRef.current = null;
      }
    }
  };

  // Function to check double insurance
  const checkDoubleInsurance = async () => {
    if (!vehicleData.chassisNumber || !vehicleData.registrationNumber) {
      setSnackbarMessage('Please enter chassis number and registration number first');
      setSnackbarType('warning');
      setShowSnackbar(true);
      return;
    }

    setDoubleInsuranceCheck(prev => ({ ...prev, isChecking: true }));

    const fromCover =
      (useNextAvailableDate && toYYYYMMDD(doubleInsuranceCheck.nextAvailableDate)) ||
      coverStartRef.current ||
      toYYYYMMDD(effectiveStartDate) ||
      toYYYYMMDD(startDate);
    const userStartDate = fromCover || new Date().toISOString().split('T')[0];
    const endFromCover =
      (useNextAvailableDate && userStartDate && coverPeriod
        ? calculateEndDate(userStartDate, coverPeriod)
        : null) ||
      coverEndRef.current ||
      toYYYYMMDD(effectiveEndDate) ||
      toYYYYMMDD(endDate);
    const userEndDate =
      coverType === 'TPO_EXTEND'
        ? (endFromCover || '')
        : userStartDate && coverPeriod
          ? calculateEndDate(userStartDate, coverPeriod)
          : (endFromCover || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
    const startFinal = toYYYYMMDD(userStartDate) || userStartDate;
    const endFinal = toYYYYMMDD(userEndDate) || userEndDate;

    try {
      const response = await fetch(getApiUrl('/api/dmvic/validate-double-insurance'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          vehicleData: {
            chassisNumber: vehicleData.chassisNumber,
            registrationNumber: vehicleData.registrationNumber
          },
          vehicleClass: selectedClass,
          policyDates: {
            startDate: startFinal,
            endDate: endFinal
          }
        })
      });

      const result = await response.json();
      
      if (result.success) {
        const canProceed = result.canProceed === true;
        setDoubleInsuranceCheck({
          isChecking: false,
          hasDoubleInsurance: result.hasDoubleInsurance,
          canProceed,
          existingPolicies: result.existingPolicies || [],
          nextAvailableDate: result.nextAvailableDate || null,
          error: null
        });
        setUseNextAvailableDate(false);

        if (result.hasDoubleInsurance) {
          if (canProceed) {
            setSnackbarMessage('✅ Your start date is valid. You may proceed to checkout.');
            setSnackbarType('success');
            setShowSnackbar(true);
          } else {
            setSnackbarMessage(`❌ Double Insurance Detected: ${result.message}`);
            setSnackbarType('error');
            setShowSnackbar(true);
          }
        } else {
          setSnackbarMessage('✅ Vehicle insurance check passed - no existing policies found');
          setSnackbarType('success');
          setShowSnackbar(true);
        }
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      setDoubleInsuranceCheck({
        isChecking: false,
        hasDoubleInsurance: false,
        existingPolicies: [],
        error: error.message
      });
      setSnackbarMessage(`Insurance check failed: ${error.message}`);
      setSnackbarType('error');
      setShowSnackbar(true);
    }
  };

  // Handler for re-entry validation fields
  const handleReEntryChange = (field, value) => {
    let processed = value;
    if (field === 'chassisNumber1' || field === 'chassisNumber2') {
      processed = String(value || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    }
    setReEntryValidation(prev => {
      const updated = { ...prev, [field]: processed };
      
      // Check if all re-entry fields match
      const chassisMatch = updated.chassisNumber1.trim().toUpperCase() === updated.chassisNumber2.trim().toUpperCase() && updated.chassisNumber1.trim() !== '';
      const engineMatch = updated.engineNumber1.trim().toUpperCase() === updated.engineNumber2.trim().toUpperCase() && updated.engineNumber1.trim() !== '';
      const yearMatch = updated.yearOfManufacture1.trim() === updated.yearOfManufacture2.trim() && updated.yearOfManufacture1.trim() !== '';
      
      updated.allMatch = chassisMatch && engineMatch && yearMatch;
      
      return updated;
    });
  };

  // Disable copy-paste on re-entry fields
  const handleReEntryPaste = (e) => {
    e.preventDefault();
    showNotification('Copy-paste is disabled. Please type the values manually.', 'warning', 3000);
  };

  const handleTonnageKeyDown = (e) => {
    // Block decimal, scientific notation, and sign keys for whole-number tonnage.
    if (['.', ',', 'e', 'E', '+', '-'].includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleTonnagePaste = (e) => {
    const pasted = e.clipboardData?.getData('text') || '';
    if (/[.,]/.test(pasted)) {
      e.preventDefault();
      showNotification('Decimals not allowed for tonnage. Please enter a whole number.', 'warning', 3000);
    }
  };

  const isFormComplete = () => {
    // For new vehicles without registration, allow chassis as registration
    const registrationField = isNewVehicle ? 'chassisNumber' : 'registrationNumber';
    const requiredFields = [registrationField, 'make', 'yearOfManufacture', 'chassisNumber', 'bodyType', 'vehicleValue'];
    const classSpecificFields = getRequiredFieldsForClass(selectedClass, coverType);
    
    // Check basic required fields
    const basicFieldsComplete = requiredFields.every(field => {
      const value = vehicleData[field];
      if (value === null || value === undefined) return false;
      if (field === 'registrationNumber') {
        if (isNewVehicle) return true;
        return validateKenyanVehicleRegistration(value, selectedClass, registrationValidationOptions()).valid;
      }
      if (field === 'vehicleValue') {
        const n = parseKesAmount(value);
        if (isCompCover) return Number.isFinite(n) && n >= COMP_GLOBAL_MIN_SUM_INSURED;
        return Number.isFinite(n) && n > 0;
      }
      // Handle different data types
      if (typeof value === 'string') {
        return value.trim() !== '';
      } else if (typeof value === 'number') {
        return value > 0; // For yearOfManufacture and other numeric fields
      }
      const stringValue = String(value).trim();
      return stringValue !== '' && stringValue !== '0';
    });
    
    // Check class-specific required fields (tonnage: reject 0 / invalid — DMVIC Type B requires real tonnes)
    const classFieldsComplete = classSpecificFields.every(field => {
      if (field === 'bodyType') return true; // Already checked in basic fields

      if (field === 'tonnage') {
        const ton = parseInt(String(vehicleData.tonnage ?? '').trim(), 10);
        if (TONNAGE_FIXED_CLASSES.has(selectedClass)) return ton === 1;
        return Number.isInteger(ton) && ton >= 1 && ton <= 999;
      }

      const value = vehicleData[field]?.toString().trim();
      if (!value) return false;

      if (field === 'licensedToCarry') {
        const num = parseInt(value, 10);
        if (isNaN(num) || num < 1) return false;
        if (selectedClass === 'PSV Chauffer Driven') return num === 3 || num === 4;
        if (selectedClass === 'Motorcycle PSV') return num === 1;
        if (selectedClass === 'Tuk Tuk PSV') return num === 3;
        if (selectedClass === 'Motor Commercial Institutional') return num >= 1 && num <= 72;
      }

      return true;
    });
    
    // Check re-entry validation if required
    if (reEntryValidation.required) {
      return basicFieldsComplete && classFieldsComplete && reEntryValidation.allMatch;
    }

    if (isCompCover) {
      const yom = parseInt(vehicleData.yearOfManufacture, 10);
      const age = new Date().getFullYear() - yom;
      if (!Number.isFinite(yom) || age > MAX_VEHICLE_AGE_YEARS) return false;
      if (!compQuote || Number(compQuote.payableAmount) <= 0) return false;
    }
    
    return basicFieldsComplete && classFieldsComplete;
  };

  const getRegistrationNumberStatus = () => {
    if (isNewVehicle) {
      return { status: 'empty', message: 'Leave blank for a new vehicle (chassis will be used)' };
    }
    const check = validateKenyanVehicleRegistration(
      vehicleData.registrationNumber,
      selectedClass,
      registrationValidationOptions()
    );
    if (check.status === 'empty') return { status: 'empty', message: 'Enter the full registration (at least 6 characters, e.g. KAA123 or KAV026M)' };
    if (!check.valid) {
      const cleaned = check.cleaned || '';
      if (cleaned.length > 0 && cleaned.length < 7) {
        return { status: 'incomplete', message: check.message };
      }
      return { status: 'invalid', message: check.message };
    }
    return { status: 'valid', message: check.message };
  };

  const getChassisNumberStatus = () => {
    const chassisNumber = vehicleData.chassisNumber;
    if (!chassisNumber) return { status: 'empty', message: 'Enter chassis number' };
    if (chassisNumber.length < 5) return { status: 'short', message: 'Too short (min 5 characters)' };
    if (chassisNumber.length > 20) return { status: 'long', message: 'Too long (max 20 characters)' };
    return { status: 'valid', message: 'Valid chassis number' };
  };

  const getYearOfManufactureStatus = () => {
    const year = vehicleData.yearOfManufacture;
    if (!year) return { status: 'empty', message: 'Enter year of manufacture' };
    
    let yearNum = parseInt(year);
    const currentYear = new Date().getFullYear();
    const maxYear = currentYear + 1;
    
    if (isNaN(yearNum)) return { status: 'invalid', message: 'Enter a valid year' };
    if (String(year).length !== 4) return { status: 'invalid', message: 'Use 4 digits (e.g. 1985)' };
    if (yearNum < 1900) return { status: 'invalid', message: 'Year must be 1900 or later' };
    if (yearNum > maxYear) return { status: 'invalid', message: `Year must be between 1900 and ${maxYear}` };
    return { status: 'valid', message: 'Valid year of manufacture' };
  };



  const getBodyTypeOptions = (selectedClass) => {
    const options = {
      'Motor Private': ['SALOON', 'SUV', 'STATION WAGON', 'PICKUP', 'HATCHBACK'],
      'Motor Commercial': ['TRUCK', 'LORRY', 'VAN', 'PICKUP', 'BUS'],
      'Motor Commercial Institutional': ['TRUCK', 'LORRY', 'VAN', 'PICKUP', 'BUS'],
      'Motor Commercial General Cartage': ['TRUCK', 'LORRY', 'VAN', 'PICKUP'],
      'Motor Commercial Own Goods': ['TRUCK', 'LORRY', 'VAN', 'PICKUP'],
      'Prime Movers': ['TRUCK', 'LORRY', 'PRIME MOVER'],
      'Tractor TPO': ['TRACTOR', 'AGRICULTURAL', 'TRAILER'],
      'Tuk Tuk Commercial': ['TUKTUK', 'THREE WHEELER', 'TUK TUK'],
      'Tuk Tuk PSV': ['TUKTUK', 'THREE WHEELER', 'TUK TUK'],
      'PSV Chauffer Driven': ['SALOON', 'SUV', 'STATION WAGON'],
      'Motorcycle Private': ['MOTORCYCLE', 'SCOOTER'],
      'Motorcycle PSV': ['MOTORCYCLE', 'SCOOTER']
    };
    
    return options[selectedClass] || ['SALOON'];
  };

  // Validation functions for new fields
  const getLicensedToCarryStatus = () => {
    const value = vehicleData.licensedToCarry;
    if (!getRequiredFieldsForClass(selectedClass, coverType).includes('licensedToCarry')) {
      return { status: 'valid', message: 'Not required for this vehicle class' };
    }
    if (!value) return { status: 'empty', message: 'Enter number of passengers' };
    const num = parseInt(value, 10);
    if (isNaN(num)) return { status: 'invalid', message: 'Enter a valid number' };

    // Motorcycle PSV: strictly 1 (prefilled, locked)
    if (selectedClass === 'Motorcycle PSV') {
      if (num !== 1) return { status: 'invalid', message: 'Motorcycle PSV is restricted to 1 passenger (PLL)' };
      return { status: 'valid', message: '1 passenger (fixed for Motorcycle PSV)' };
    }
    if (selectedClass === 'Tuk Tuk PSV') {
      if (num !== 3) return { status: 'invalid', message: 'Tuk Tuk PSV is restricted to 3 passengers (PLL)' };
      return { status: 'valid', message: '3 passengers (fixed for Tuk Tuk PSV)' };
    }
    // PSV Chauffer Driven: PLL 3 or 4 only
    if (selectedClass === 'PSV Chauffer Driven') {
      if (num !== 3 && num !== 4) return { status: 'invalid', message: 'PSV Chauffer Driven: 3 or 4 passengers (PLL) only.' };
      return { status: 'valid', message: `${num} passengers (PLL 3 or 4 only)` };
    }
    // Motor Commercial Institutional: 1–72 (user input), PLL 250 (schools) or 500 (other) /pax
    if (selectedClass === 'Motor Commercial Institutional') {
      if (num < 1 || num > 72) return { status: 'invalid', message: 'Enter a number between 1 and 72 passengers' };
      return { status: 'valid', message: `${num} passengers (${institutionalPllLabel(institutionType || initialData?.institutionType)} PLL KES ${INSTITUTIONAL_PLL_FEE} × ${num})` };
    }
    // Other classes
    if (num < 1) return { status: 'invalid', message: 'Must be at least 1' };
    if (num > 50) return { status: 'invalid', message: 'Maximum 50 passengers' };
    return { status: 'valid', message: 'Valid passenger capacity' };
  };

  const getTonnageStatus = () => {
    const value = vehicleData.tonnage;
    if (!getRequiredFieldsForClass(selectedClass, coverType).includes('tonnage')) {
      return { status: 'valid', message: 'Not required for this vehicle class' };
    }
    if (!value) return { status: 'empty', message: 'Enter tonnage' };
    const num = parseInt(value, 10);
    if (TONNAGE_FIXED_CLASSES.has(selectedClass)) {
      if (num !== 1) return { status: 'invalid', message: 'Fixed at 1 tonne' };
      return { status: 'valid', message: '1 tonne (fixed)' };
    }
    if (!Number.isInteger(num) || num < 1) return { status: 'invalid', message: 'Use whole tonnes (minimum 1)' };
    if (num > 999) return { status: 'invalid', message: 'Maximum 999 tonnes' };
    return { status: 'valid', message: 'Valid tonnage (whole number)' };
  };

  const regStatus = getRegistrationNumberStatus();
  const chassisStatus = getChassisNumberStatus();
  const licensedToCarryStatus = getLicensedToCarryStatus();
  const tonnageStatus = getTonnageStatus();

  return (
    <div className="vehicle-details">
      <h1 className="main-title">Vehicle Information</h1>
      <p className="subtitle">
        Please provide your vehicle details for {coverType} cover - {selectedClass}
      </p>
      

      
      <div className="form-section">
        <h3>🚗 Vehicle Details</h3>
        
        <div className="form-group">
          <label htmlFor="registrationNumber">
            {isNewVehicle ? 'Chassis Number (as Registration) *' : 'Registration Number *'}
          </label>
          <input
            type="text"
            id="registrationNumber"
            name="registrationNumber"
            value={vehicleData.registrationNumber}
            onChange={(e) => handleInputChange('registrationNumber', e.target.value)}
            onFocus={() => handleFocus('registrationNumber')}
            onBlur={(e) => {
              handleBlur('registrationNumber');
              const nextId = e.relatedTarget && e.relatedTarget.id;
              if (nextId === 'lookup-vehicle-btn' || nextId === 'validate-vehicle-btn') return;
              const reg = normalizeKenyanRegistration(e.target.value);
              if (isRegistrationCompleteForNtsaLookup(reg, selectedClass)) {
                searchVehicleDetails(reg);
              }
            }}
            placeholder={
              isNewVehicle
                ? 'Enter chassis number (will be used as registration)'
                : isTractorTPO
                  ? 'e.g., KTCC604K'
                  : isTukTukClass
                    ? 'e.g., KTW123A'
                    : 'e.g., KAA123, KAV026M, KMFB213R'
            }
            required
            {...(isTractorTPO ? {} : { maxLength: 10 })}
          />
          {isNewVehicle && (
            <small style={{ color: '#856404', fontStyle: 'italic' }}>
              ⚠️ New vehicle: Chassis number will be used as registration number for DMVIC submission
            </small>
          )}
          <div className="input-status">
            <span className={`status-indicator ${regStatus.status}`}>
              {regStatus.status === 'valid' ? '✅' : 
               regStatus.status === 'empty' ? '⭕' : '⚠️'}
            </span>
            <span className={`status-message ${regStatus.status}`}>
              {regStatus.message}
            </span>
          </div>
          {regStatus.status === 'invalid' && /KTW/.test(regStatus.message || '') && (
            <div className="search-status error" role="alert" style={{ marginTop: 8 }}>
              <span className="search-indicator">❌</span>
              <span className="search-message">
                {regStatus.message} Continue is blocked until the plate starts with KTW.
              </span>
            </div>
          )}
          
          {isRegistrationCompleteForNtsaLookup(vehicleData.registrationNumber, selectedClass) && !vehicleSearch.isSearching && !vehicleSearch.error && (
            <div style={{ marginTop: 8 }}>
              <button
                type="button"
                id="lookup-vehicle-btn"
                className="validate-retry-btn"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => searchVehicleDetails(vehicleData.registrationNumber, { force: true })}
              >
                Look up vehicle
              </button>
            </div>
          )}
          
          {/* Vehicle Search Status */}
          {vehicleSearch.isSearching && (
            <div className="search-status searching">
              <span className="search-indicator">🔍</span>
              <span className="search-message">
                {vehicleSearch.progressMessage || 'Looking up vehicle details…'}
              </span>
            </div>
          )}
          
          {vehicleSearch.found && !vehicleSearch.isSearching && !vehicleSearch.error && (
            <div className="search-status found">
              <span className="search-indicator">✅</span>
              <span className="search-message">
                {vehicleData.make || vehicleData.model || vehicleData.yearOfManufacture 
                  ? 'Vehicle found and details auto-filled' 
                  : 'Vehicle found in DMVIC database (check policy history)'}
              </span>
            </div>
          )}
          
          {vehicleSearch.error && !vehicleSearch.isSearching && (
            <div className="search-status error">
              <span className="search-indicator">⚠️</span>
              <span className="search-message">{vehicleSearch.error}</span>
              {!/KTW/.test(vehicleSearch.error || '') && (
                <button
                  type="button"
                  id="validate-vehicle-btn"
                  className="validate-retry-btn"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() =>
                    isRegistrationCompleteForNtsaLookup(vehicleData.registrationNumber, selectedClass) &&
                    searchVehicleDetails(vehicleData.registrationNumber.trim(), { force: true })
                  }
                  disabled={!isRegistrationCompleteForNtsaLookup(vehicleData.registrationNumber, selectedClass)}
                  title="Retry vehicle search"
                >
                  Validate
                </button>
              )}
            </div>
          )}
          <small>
            {isTractorTPO
              ? 'Tractor TPO: registration must start with KTC and be at least 4 characters. Spaces/special characters are removed automatically.'
              : isTukTukClass
                ? `${selectedClass || 'Tuk Tuk'}: registration must start with KTW (Kenya Three-Wheelers). Do not continue until the plate starts with KTW. Spaces/special characters are removed automatically.`
                : 'Enter the full registration, then leave this field or click Look up vehicle. A search does not run while you are still typing. At least 6 characters (older plates: KAA123; current: KAV026M; motorcycle: KMFB213R). Shorter prefixes like KAV are blocked.'}
          </small>
        </div>
        
        <div className="form-group">
          <label htmlFor="make">Vehicle Make *</label>
          <input
            type="text"
            id="make"
            name="make"
            value={vehicleData.make}
            onChange={(e) => handleInputChange('make', e.target.value)}
            onFocus={() => handleFocus('make')}
            onBlur={() => handleBlur('make')}
            placeholder="e.g., Toyota, Honda, Ford"
            required
          />
          <small>Enter the manufacturer/brand of your vehicle</small>
        </div>
        
        <div className="form-group">
          <label htmlFor="yearOfManufacture">Year of Manufacture *</label>
          <input
            type="number"
            id="yearOfManufacture"
            name="yearOfManufacture"
            value={vehicleData.yearOfManufacture}
            onChange={(e) => handleInputChange('yearOfManufacture', e.target.value)}
            onFocus={() => handleFocus('yearOfManufacture')}
            onBlur={() => handleBlur('yearOfManufacture')}
            placeholder="e.g., 2020"
            min="1900"
            max={new Date().getFullYear() + 1}
            required
          />
          <div className="input-status">
            <span className={`status-indicator ${getYearOfManufactureStatus().status}`}>
              {getYearOfManufactureStatus().status === 'valid' ? '✅' : 
               getYearOfManufactureStatus().status === 'empty' ? '⭕' : '⚠️'}
            </span>
            <span className={`status-message ${getYearOfManufactureStatus().status}`}>
              {getYearOfManufactureStatus().message}
            </span>
          </div>
          <small>Enter a 4-digit year between 1900 and {new Date().getFullYear() + 1}.</small>
        </div>
        
        <div className="form-group">
          <label htmlFor="chassisNumber">Chassis Number *</label>
          <input
            type="text"
            id="chassisNumber"
            name="chassisNumber"
            value={chassisFromDmvic && !chassisInputFocused && vehicleData.chassisNumber
              ? maskChassisNumber(vehicleData.chassisNumber)
              : vehicleData.chassisNumber}
            onChange={(e) => handleInputChange('chassisNumber', e.target.value)}
            onFocus={() => { setChassisInputFocused(true); handleFocus('chassisNumber'); }}
            onBlur={() => { setChassisInputFocused(false); handleBlur('chassisNumber'); }}
            placeholder="Enter vehicle chassis number"
            required
            maxLength={20}
          />
          <div className="input-status">
            <span className={`status-indicator ${chassisStatus.status}`}>
              {chassisStatus.status === 'valid' ? '✅' : 
               chassisStatus.status === 'empty' ? '⭕' : '⚠️'}
            </span>
            <span className={`status-message ${chassisStatus.status}`}>
              {chassisStatus.message}
            </span>
          </div>
          <small>Hyphens and special characters are removed automatically. Minimum 5 characters required.</small>
        </div>

        {/* Re-entry Validation Section - Shown when vehicle not found */}
        {reEntryValidation.required && (
          <div className="form-section" style={{ 
            marginTop: '20px', 
            padding: '15px', 
            background: '#fff3cd', 
            border: '1px solid #ffeaa7', 
            borderRadius: '4px' 
          }}>
            <h4 style={{ color: '#856404', margin: '0 0 15px 0' }}>
              ⚠️ Vehicle Not Found - Re-enter for Verification
            </h4>
            <p style={{ color: '#856404', margin: '0 0 15px 0', fontSize: '14px' }}>
              Please re-enter the following details to verify accuracy. Both entries must match exactly.
            </p>
            
            <div className="form-group">
              <label htmlFor="chassisNumber1">Chassis Number (First Entry) *</label>
              <input
                type="text"
                id="chassisNumber1"
                value={reEntryValidation.chassisNumber1}
                onChange={(e) => handleReEntryChange('chassisNumber1', e.target.value)}
                onPaste={handleReEntryPaste}
                onCopy={(e) => e.preventDefault()}
                placeholder="Enter chassis number"
                required
                maxLength={20}
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="chassisNumber2">Chassis Number (Re-enter) *</label>
              <input
                type="text"
                id="chassisNumber2"
                value={reEntryValidation.chassisNumber2}
                onChange={(e) => handleReEntryChange('chassisNumber2', e.target.value)}
                onPaste={handleReEntryPaste}
                onCopy={(e) => e.preventDefault()}
                placeholder="Re-enter chassis number"
                required
                maxLength={20}
                style={{ textTransform: 'uppercase' }}
              />
              {reEntryValidation.chassisNumber2 && (
                <div className="input-status">
                  <span className={`status-indicator ${reEntryValidation.chassisNumber1.trim().toUpperCase() === reEntryValidation.chassisNumber2.trim().toUpperCase() && reEntryValidation.chassisNumber1.trim() !== '' ? 'valid' : 'invalid'}`}>
                    {reEntryValidation.chassisNumber1.trim().toUpperCase() === reEntryValidation.chassisNumber2.trim().toUpperCase() && reEntryValidation.chassisNumber1.trim() !== '' ? '✅' : '⚠️'}
                  </span>
                  <span className={`status-message ${reEntryValidation.chassisNumber1.trim().toUpperCase() === reEntryValidation.chassisNumber2.trim().toUpperCase() && reEntryValidation.chassisNumber1.trim() !== '' ? 'valid' : 'invalid'}`}>
                    {reEntryValidation.chassisNumber1.trim().toUpperCase() === reEntryValidation.chassisNumber2.trim().toUpperCase() && reEntryValidation.chassisNumber1.trim() !== '' ? 'Chassis numbers match' : 'Chassis numbers do not match'}
                  </span>
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="engineNumber1">Engine Number (First Entry) *</label>
              <input
                type="text"
                id="engineNumber1"
                value={reEntryValidation.engineNumber1}
                onChange={(e) => handleReEntryChange('engineNumber1', e.target.value)}
                onPaste={handleReEntryPaste}
                onCopy={(e) => e.preventDefault()}
                placeholder="Enter engine number"
                required
                maxLength={50}
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="engineNumber2">Engine Number (Re-enter) *</label>
              <input
                type="text"
                id="engineNumber2"
                value={reEntryValidation.engineNumber2}
                onChange={(e) => handleReEntryChange('engineNumber2', e.target.value)}
                onPaste={handleReEntryPaste}
                onCopy={(e) => e.preventDefault()}
                placeholder="Re-enter engine number"
                required
                maxLength={50}
                style={{ textTransform: 'uppercase' }}
              />
              {reEntryValidation.engineNumber2 && (
                <div className="input-status">
                  <span className={`status-indicator ${reEntryValidation.engineNumber1.trim().toUpperCase() === reEntryValidation.engineNumber2.trim().toUpperCase() && reEntryValidation.engineNumber1.trim() !== '' ? 'valid' : 'invalid'}`}>
                    {reEntryValidation.engineNumber1.trim().toUpperCase() === reEntryValidation.engineNumber2.trim().toUpperCase() && reEntryValidation.engineNumber1.trim() !== '' ? '✅' : '⚠️'}
                  </span>
                  <span className={`status-message ${reEntryValidation.engineNumber1.trim().toUpperCase() === reEntryValidation.engineNumber2.trim().toUpperCase() && reEntryValidation.engineNumber1.trim() !== '' ? 'valid' : 'invalid'}`}>
                    {reEntryValidation.engineNumber1.trim().toUpperCase() === reEntryValidation.engineNumber2.trim().toUpperCase() && reEntryValidation.engineNumber1.trim() !== '' ? 'Engine numbers match' : 'Engine numbers do not match'}
                  </span>
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="yearOfManufacture1">Year of Manufacture (First Entry) *</label>
              <input
                type="number"
                id="yearOfManufacture1"
                value={reEntryValidation.yearOfManufacture1}
                onChange={(e) => handleReEntryChange('yearOfManufacture1', e.target.value)}
                onPaste={handleReEntryPaste}
                onCopy={(e) => e.preventDefault()}
                placeholder="Enter year"
                required
                min="1900"
                max={new Date().getFullYear()}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="yearOfManufacture2">Year of Manufacture (Re-enter) *</label>
              <input
                type="number"
                id="yearOfManufacture2"
                value={reEntryValidation.yearOfManufacture2}
                onChange={(e) => handleReEntryChange('yearOfManufacture2', e.target.value)}
                onPaste={handleReEntryPaste}
                onCopy={(e) => e.preventDefault()}
                placeholder="Re-enter year"
                required
                min="1900"
                max={new Date().getFullYear()}
              />
              {reEntryValidation.yearOfManufacture2 && (
                <div className="input-status">
                  <span className={`status-indicator ${reEntryValidation.yearOfManufacture1.trim() === reEntryValidation.yearOfManufacture2.trim() && reEntryValidation.yearOfManufacture1.trim() !== '' ? 'valid' : 'invalid'}`}>
                    {reEntryValidation.yearOfManufacture1.trim() === reEntryValidation.yearOfManufacture2.trim() && reEntryValidation.yearOfManufacture1.trim() !== '' ? '✅' : '⚠️'}
                  </span>
                  <span className={`status-message ${reEntryValidation.yearOfManufacture1.trim() === reEntryValidation.yearOfManufacture2.trim() && reEntryValidation.yearOfManufacture1.trim() !== '' ? 'valid' : 'invalid'}`}>
                    {reEntryValidation.yearOfManufacture1.trim() === reEntryValidation.yearOfManufacture2.trim() && reEntryValidation.yearOfManufacture1.trim() !== '' ? 'Years match' : 'Years do not match'}
                  </span>
                </div>
              )}
            </div>

            {reEntryValidation.allMatch && (
              <div style={{ 
                marginTop: '10px', 
                padding: '10px', 
                background: '#d4edda', 
                border: '1px solid #c3e6cb', 
                borderRadius: '4px',
                color: '#155724'
              }}>
                ✅ All entries match. Verification complete.
              </div>
            )}
          </div>
        )}

        {/* Double Insurance Check */}
        {vehicleData.chassisNumber && vehicleData.registrationNumber && (
          <div className="form-group">
            <p style={{ fontSize: '13px', color: '#555', marginBottom: '8px' }}>
              <strong>Policy period being checked:</strong> {formatDate(coverStartRef.current || startDate || effectiveStartDate)} → {formatDate(coverEndRef.current || endDate || effectiveEndDate)}
              {!(coverStartRef.current || startDate || effectiveStartDate) && <span style={{ color: '#856404' }}> (go back to Cover Details and select a start date)</span>}
            </p>
            <button
              type="button"
              className="btn-secondary"
              onClick={checkDoubleInsurance}
              disabled={doubleInsuranceCheck.isChecking}
              style={{ marginBottom: '10px' }}
            >
              {doubleInsuranceCheck.isChecking ? '🔍 Checking...' : '🔍 Check Existing Insurance'}
            </button>
            {doubleInsuranceCheck.hasDoubleInsurance && (
              <div className="warning-message" style={{ 
                background: doubleInsuranceCheck.canProceed ? '#d4edda' : '#fff3cd', 
                border: doubleInsuranceCheck.canProceed ? '1px solid #c3e6cb' : '1px solid #ffeaa7', 
                borderRadius: '4px', 
                padding: '10px', 
                marginTop: '10px' 
              }}>
                {doubleInsuranceCheck.canProceed && (
                  <div style={{ marginBottom: '12px', padding: '10px', background: '#c3e6cb', borderRadius: '4px', color: '#155724', fontWeight: 'bold' }}>
                    ✓ Your chosen start date is valid. You may proceed to checkout.
                  </div>
                )}
                <h4 style={{ color: doubleInsuranceCheck.canProceed ? '#155724' : '#856404', margin: '0 0 10px 0' }}>{doubleInsuranceCheck.canProceed ? 'Existing policy ends before your start date' : '⚠️ Existing Insurance Found'}</h4>
                <p style={{ color: doubleInsuranceCheck.canProceed ? '#155724' : '#856404', margin: '0 0 15px 0', fontSize: '14px' }}>
                  {doubleInsuranceCheck.canProceed ? 'Details of the expiring policy are below. You can continue to checkout.' : 'This vehicle already has active insurance coverage. Please check the details below:'}
                </p>
                {/* Only show ACTIVE policies - expired policies are filtered out by backend */}
                {doubleInsuranceCheck.existingPolicies
                  .filter(policy => policy.status === 'Active') // Extra safety filter
                  .map((policy, index) => (
                  <div key={index} className="existing-policy" style={{ 
                    marginBottom: '15px', 
                    padding: '10px', 
                    background: '#f8f9fa', 
                    border: '1px solid #dee2e6', 
                    borderRadius: '4px' 
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <h5 style={{ margin: '0', color: '#495057' }}>
                        {policy.policyNumber || policy.InsuranceCertificateNo || `Policy ${index + 1}`}
                      </h5>
                      <span style={{ 
                        padding: '2px 8px', 
                        borderRadius: '12px', 
                        fontSize: '12px',
                        fontWeight: 'bold',
                        background: policy.status === 'Active' ? '#d4edda' : '#f8d7da',
                        color: policy.status === 'Active' ? '#155724' : '#721c24'
                      }}>
                        {policy.status || 'Active'}
                      </span>
                    </div>
                    <p style={{ margin: '4px 0', fontSize: '14px' }}>
                      <strong>Insurance Company:</strong> {policy.companyName || policy.MemberCompanyName || 'Unknown'}
                    </p>
                    <p style={{ margin: '4px 0', fontSize: '14px' }}>
                      <strong>Cover Type:</strong> {policy.coverType || policy.TypeOfCover || 'Not specified'}
                    </p>
                    <div style={{ display: 'flex', gap: '20px', marginTop: '8px' }}>
                      <p style={{ margin: '4px 0', fontSize: '14px' }}>
                        <strong>Start Date:</strong> {formatDate(policy.startDate || policy.CoverStartDate)}
                      </p>
                      <p style={{ margin: '4px 0', fontSize: '14px' }}>
                        <strong>End Date:</strong> {formatDate(policy.endDate || policy.CoverEndDate)}
                      </p>
                    </div>
                    {policy.status === 'Active' && (
                      <div style={{ 
                        marginTop: '8px', 
                        padding: '8px', 
                        background: '#fff3cd', 
                        border: '1px solid #ffeaa7', 
                        borderRadius: '4px',
                        fontSize: '13px',
                        color: '#856404'
                      }}>
                        ⚠️ <strong>Active Policy:</strong> This vehicle is currently insured and cannot be covered by another policy until the current one expires.
                      </div>
                    )}
                  </div>
                ))}
                {/* When next available date is beyond 30 days, backend does not return it - show clear message only (unless API said user can proceed) */}
                {!doubleInsuranceCheck.canProceed && !doubleInsuranceCheck.nextAvailableDate && doubleInsuranceCheck.existingPolicies?.length > 0 && (
                  <div style={{ 
                    marginTop: '10px', 
                    padding: '15px', 
                    background: '#f8d7da', 
                    border: '1px solid #f5c6cb', 
                    borderRadius: '4px', 
                    fontSize: '14px', 
                    color: '#721c24' 
                  }}>
                    <p style={{ margin: '0', fontWeight: 'bold' }}>You cannot proceed.</p>
                    <p style={{ margin: '8px 0 0 0' }}>
                      The next available start date (day after your current policy expires) is more than 30 days away. We can only suggest start dates within 30 days. Please try again when your current policy is within 30 days of expiry, or contact D-MVIC support.
                    </p>
                  </div>
                )}
                {doubleInsuranceCheck.nextAvailableDate && (
                  <div style={{ 
                    marginTop: '10px', 
                    padding: '15px', 
                    background: '#e7f3ff', 
                    border: '1px solid #b3d9ff', 
                    borderRadius: '4px', 
                    fontSize: '14px', 
                    color: '#004085' 
                  }}>
                    <div style={{ marginBottom: '10px' }}>
                      <strong>Next Available Date:</strong> {formatDate(doubleInsuranceCheck.nextAvailableDate)} (day after previous policy expiry)
                    </div>
                    {(() => {
                      const chosenNorm = coverStartRef.current || toYYYYMMDD(startDate) || toYYYYMMDD(effectiveStartDate);
                      const nextNorm = toYYYYMMDD(doubleInsuranceCheck.nextAvailableDate) || doubleInsuranceCheck.nextAvailableDate;
                      const canProceed = chosenNorm && nextNorm && chosenNorm >= nextNorm;
                      return canProceed ? (
                        <div style={{ marginTop: '10px', padding: '10px', background: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '4px', fontSize: '14px', color: '#155724' }}>
                          ✓ Your chosen start date ({formatDate(chosenNorm)}) is on or after the next available date. You can proceed to checkout.
                        </div>
                      ) : null;
                    })()}
                    <div style={{ 
                      marginTop: '10px', 
                      padding: '10px', 
                      background: '#ffffff', 
                      border: '1px solid #b3d9ff', 
                      borderRadius: '4px' 
                    }}>
                      <p style={{ margin: '0 0 10px 0', fontWeight: 'bold' }}>
                        Would you like to use this date as your policy start date?
                      </p>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <button
                          type="button"
                          onClick={() => {
                            setUseNextAvailableDate(true);
                            if (doubleInsuranceCheck.nextAvailableDate) {
                              const newStartDate = toYYYYMMDD(doubleInsuranceCheck.nextAvailableDate) || doubleInsuranceCheck.nextAvailableDate;
                              const period = coverPeriod || (coverType === 'TOR' ? '1 Month' : '1 Year');
                              const newEndDate = calculateEndDate(newStartDate, period);
                              coverStartRef.current = newStartDate;
                              coverEndRef.current = newEndDate;
                              setEffectiveStartDate(newStartDate);
                              setEffectiveEndDate(newEndDate);
                              setDoubleInsuranceCheck(prev => ({
                                ...prev,
                                hasDoubleInsurance: true,
                                canProceed: true,
                                nextAvailableDate: newStartDate
                              }));
                            }
                          }}
                          style={{
                            padding: '8px 20px',
                            backgroundColor: useNextAvailableDate ? '#28a745' : '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold'
                          }}
                        >
                          ✓ Yes, use {formatDate(doubleInsuranceCheck.nextAvailableDate)}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setUseNextAvailableDate(false);
                            setEffectiveStartDate(startDate);
                            setEffectiveEndDate(endDate);
                          }}
                          style={{
                            padding: '8px 20px',
                            backgroundColor: !useNextAvailableDate ? '#dc3545' : '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold'
                          }}
                        >
                          ✗ No, I&apos;ll choose my own date
                        </button>
                      </div>
                      {useNextAvailableDate && (
                        <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: '#28a745', fontStyle: 'italic' }}>
                          ✓ Your policy start date will be automatically set to {formatDate(doubleInsuranceCheck.nextAvailableDate)} in the next step.
                        </p>
                      )}
                      {!useNextAvailableDate && doubleInsuranceCheck.nextAvailableDate && (
                        <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: '#856404', fontStyle: 'italic' }}>
                          ⚠️ You&apos;ll need to go back to Cover Details and select a start date on or after {formatDate(doubleInsuranceCheck.nextAvailableDate)} to proceed.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="bodyType">Body Type *</label>
          <input
            type="text"
            id="bodyType"
            name="bodyType"
            value={vehicleData.bodyType}
            onChange={(e) => handleInputChange('bodyType', e.target.value)}
            onFocus={() => handleFocus('bodyType')}
            onBlur={() => handleBlur('bodyType')}
            placeholder="e.g., SALOON, SUV, STATION WAGON"
            required
          />
          <small>Enter the body type of your vehicle (will auto-fill from DMVIC if found)</small>
        </div>

        <div className="form-group">
          <label htmlFor="engineNumber">Engine Number</label>
          <input
            type="text"
            id="engineNumber"
            name="engineNumber"
            value={vehicleData.engineNumber}
            onChange={(e) => handleInputChange('engineNumber', e.target.value)}
            onFocus={() => handleFocus('engineNumber')}
            onBlur={() => handleBlur('engineNumber')}
            placeholder="Enter engine number (optional)"
            maxLength={50}
          />
          <small>Engine number is optional but recommended for better identification</small>
        </div>

        <div className="form-group">
          <label htmlFor="vehicleValue">{isCompCover ? 'Sum insured (KES) *' : 'Vehicle value (KES) *'}</label>
          <input
            type="text"
            inputMode="numeric"
            id="vehicleValue"
            name="vehicleValue"
            value={vehicleData.vehicleValue}
            onChange={(e) => handleInputChange('vehicleValue', e.target.value)}
            onFocus={() => handleFocus('vehicleValue')}
            onBlur={() => handleBlur('vehicleValue')}
            placeholder={isCompCover ? 'e.g. 1,000,000' : 'e.g. 1500000'}
            required
          />
          <small>
            {isCompCover
              ? `Required. Minimum sum insured KES ${COMP_GLOBAL_MIN_SUM_INSURED.toLocaleString('en-KE')}. Vehicle age must be ${MAX_VEHICLE_AGE_YEARS} years or below.`
              : 'Required. Estimated value of the vehicle (sum insured). Used in the policy and sent to Core/TQ on issuance.'}
          </small>
          {isCompCover && vehicleData.vehicleValue && parseKesAmount(vehicleData.vehicleValue) < COMP_GLOBAL_MIN_SUM_INSURED && (
            <div role="alert" style={{ marginTop: 8, color: '#842029', fontSize: 13 }}>
              Sum insured must be at least KES {COMP_GLOBAL_MIN_SUM_INSURED.toLocaleString('en-KE')}.
            </div>
          )}
        </div>

        {isCompCover && (
          <ComprehensiveBenefitsSection
            vehicleClass={selectedClass}
            benefits={compBenefits}
            onChangeBenefits={setCompBenefits}
            quote={compQuote}
            error={compQuoteError}
          />
        )}

        {/* Conditional fields based on vehicle class */}
        {getRequiredFieldsForClass(selectedClass, coverType).includes('licensedToCarry') && (
          <div className="form-group">
            <label htmlFor="licensedToCarry">
              Licensed to Carry (PLL – Passengers) *
              {isPllRestrictedClass(selectedClass) && (
                <span className="pll-restricted-badge" style={{ marginLeft: '8px', fontSize: '12px', color: '#6c757d', fontWeight: 'normal' }}>
                  Fixed for this class
                </span>
              )}
            </label>
            <input
              type="number"
              id="licensedToCarry"
              name="licensedToCarry"
              value={vehicleData.licensedToCarry}
              onChange={(e) => handleInputChange('licensedToCarry', e.target.value)}
              onFocus={() => handleFocus('licensedToCarry')}
              onBlur={() => handleBlur('licensedToCarry')}
              readOnly={isPllRestrictedClass(selectedClass)}
              placeholder={
                selectedClass === 'Motorcycle PSV' ? '1' :
                selectedClass === 'Tuk Tuk PSV' ? '3' :
                selectedClass === 'PSV Chauffer Driven' ? '3 or 4' :
                selectedClass === 'Motor Commercial Institutional' ? 'Enter number (1–72)' : 'Enter number of passengers'
              }
              min={selectedClass === 'Motorcycle PSV' ? 1 : selectedClass === 'Tuk Tuk PSV' ? 3 : selectedClass === 'PSV Chauffer Driven' ? 3 : selectedClass === 'Motor Commercial Institutional' ? 1 : 1}
              max={selectedClass === 'Motorcycle PSV' ? 1 : selectedClass === 'Tuk Tuk PSV' ? 3 : selectedClass === 'PSV Chauffer Driven' ? 4 : selectedClass === 'Motor Commercial Institutional' ? INSTITUTIONAL_PLL_MAX : 50}
              required
              className={isPllRestrictedClass(selectedClass) ? 'input-restricted' : ''}
              style={isPllRestrictedClass(selectedClass) ? { backgroundColor: '#e9ecef', color: '#495057', cursor: 'not-allowed' } : undefined}
            />
            <div className="input-status">
              <span className={`status-indicator ${licensedToCarryStatus.status}`}>
                {licensedToCarryStatus.status === 'valid' ? '✅' : licensedToCarryStatus.status === 'empty' ? '⭕' : '⚠️'}
              </span>
              <span className={`status-message ${licensedToCarryStatus.status}`}>
                {licensedToCarryStatus.message}
              </span>
            </div>
            {selectedClass === 'PSV Chauffer Driven' && (parseInt(vehicleData.licensedToCarry, 10) < 3 || parseInt(vehicleData.licensedToCarry, 10) > 4) && vehicleData.licensedToCarry !== '' && (
              <div role="alert" className="pll-mailto-block" style={{ marginTop: '10px', padding: '12px', background: '#f8d7da', border: '1px solid #f5c2c7', borderRadius: '6px', color: '#842029' }}>
                <p style={{ margin: '0 0 8px 0', fontWeight: '600' }}>PSV Chauffer Driven: 3 or 4 passengers (PLL) only.</p>
                <p style={{ margin: '0 0 8px 0' }}>For other passenger counts, please contact us.</p>
                <a href="mailto:requests@monarchinsurance.co.ke?subject=PSV%20PLL%20inquiry" style={{ color: '#0d6efd', fontWeight: '600' }}>
                  Email requests@monarchinsurance.co.ke
                </a>
              </div>
            )}
            <small>
              {selectedClass === 'Motorcycle PSV' && 'Motorcycle PSV: 1 passenger (PLL) – fixed.'}
              {selectedClass === 'Tuk Tuk PSV' && 'Tuk Tuk PSV: 3 passengers (PLL) – fixed; premium includes PLL.'}
              {selectedClass === 'PSV Chauffer Driven' && 'PSV Chauffer Driven: 3 or 4 passengers (PLL) only.'}
              {selectedClass === 'Motor Commercial Institutional' && `Motor Commercial Institutional: enter number of passengers (max ${INSTITUTIONAL_PLL_MAX}). PLL KES ${INSTITUTIONAL_PLL_FEE} per person.`}
              {!isPllRestrictedClass(selectedClass) && selectedClass !== 'Motor Commercial Institutional' && selectedClass !== 'PSV Chauffer Driven' && 'Number of passengers the vehicle is licensed to carry.'}
            </small>
          </div>
        )}

        {getRequiredFieldsForClass(selectedClass, coverType).includes('tonnage') && (
          <div className="form-group">
            <label htmlFor="tonnage">Tonnage *</label>
            <input
              type="number"
              id="tonnage"
              name="tonnage"
              value={TONNAGE_FIXED_CLASSES.has(selectedClass) ? FIXED_TONNAGE : vehicleData.tonnage}
              onChange={(e) => handleInputChange('tonnage', e.target.value)}
              onKeyDown={handleTonnageKeyDown}
              onPaste={handleTonnagePaste}
              onFocus={() => handleFocus('tonnage')}
              onBlur={() => handleBlur('tonnage')}
              placeholder={TONNAGE_FIXED_CLASSES.has(selectedClass) ? '1' : 'Enter tonnage'}
              min="1"
              max={TONNAGE_FIXED_CLASSES.has(selectedClass) ? '1' : '999'}
              step="1"
              required
              readOnly={TONNAGE_FIXED_CLASSES.has(selectedClass)}
              disabled={TONNAGE_FIXED_CLASSES.has(selectedClass)}
              className={TONNAGE_FIXED_CLASSES.has(selectedClass) ? 'input-restricted' : ''}
              style={TONNAGE_FIXED_CLASSES.has(selectedClass) ? { backgroundColor: '#e9ecef', color: '#495057', cursor: 'not-allowed' } : undefined}
            />
            <div className="input-status">
              <span className={`status-indicator ${tonnageStatus.status}`}>
                {tonnageStatus.status === 'valid' ? '✅' : 
                 tonnageStatus.status === 'empty' ? '⭕' : '⚠️'}
              </span>
              <span className={`status-message ${tonnageStatus.status}`}>
                {tonnageStatus.message}
              </span>
            </div>
            <small>
              {TONNAGE_FIXED_CLASSES.has(selectedClass)
                ? 'Vehicle tonnage is fixed at 1 tonne for Tuk Tuk Commercial and is not editable.'
                : 'Vehicle tonnage in whole tonnes (1–999; decimals are not allowed).'}
            </small>
          </div>
        )}

        {/* PSV Pricing Display for PSV Chauffer Driven – uses rate from DB. TOR: PLL 100/pax; TPO 1Y: PLL 500/pax (base 5,500 gross + 500 per passenger gross). */}
        {selectedClass === 'PSV Chauffer Driven' && coverType !== 'TPO' && vehicleData.licensedToCarry && licensedToCarryStatus.status === 'valid' && (
          (() => {
            const matchingRate = Array.isArray(pricing)
              ? pricing.find(p => p.label === selectedClass && p.period === coverPeriod)
              : null;
            const baseAmount = matchingRate?.amount ?? (coverType === 'TOR' ? 1000 : 5500);
            const pllFeePerPax = matchingRate?.passengerFeePerPax != null
              ? Number(matchingRate.passengerFeePerPax)
              : (coverType === 'TOR' ? 100 : 500);
            const n = parseInt(vehicleData.licensedToCarry, 10) || 0;
            const showPll = pllFeePerPax > 0 && n > 0;
            const pllAmount = n * pllFeePerPax;
            const totalAmount = baseAmount + pllAmount;
            const periodLabel = coverPeriod === '1 Month' ? 'Monthly' : 'Annual';
            return (
              <div className="form-section">
                <div className="pricing-display">
                  <h3>💰 Premium Calculation</h3>
                  <div className="pricing-card">
                    <div className="pricing-row">
                      <span className="pricing-label">Vehicle Class:</span>
                      <span className="pricing-value">{selectedClass}</span>
                    </div>
                    <div className="pricing-row">
                      <span className="pricing-label">Base Premium:</span>
                      <span className="pricing-value">KES {Number(baseAmount).toLocaleString()}</span>
                    </div>
                    <div className="pricing-row">
                      <span className="pricing-label">Passenger Capacity:</span>
                      <span className="pricing-value">{vehicleData.licensedToCarry} passengers</span>
                    </div>
                    {showPll && (
                      <div className="pricing-row">
                        <span className="pricing-label">PLL ({pllFeePerPax} per passenger):</span>
                        <span className="pricing-value">KES {Number(pllAmount).toLocaleString()}</span>
                      </div>
                    )}
                    <div className="pricing-row total">
                      <span className="pricing-label">{periodLabel} Premium:</span>
                      <span className="pricing-amount">KES {Number(totalAmount).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()
        )}

      </div>

      {/* Insurance Check Status */}
      {isCheckingInsurance && (
        <div className="form-section">
          <h3>🔍 Insurance Verification</h3>
          <div className="insurance-check-status">
            <div className="check-icon">⏳</div>
            <div className="check-content">
              <h4>Validating...</h4>
              <p>Validating...</p>
              <div className="loading-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        </div>
      )}

      {insuranceCheckComplete && (
        <div className="form-section">
          <h3>✅ Insurance Check Complete</h3>
          <div className="insurance-result">
            <div className="result-icon">✅</div>
            <div className="result-content">
              <h4>No Active Insurance Found</h4>
              <p>This vehicle is eligible for new insurance coverage.</p>
            </div>
          </div>
        </div>
      )}
      
      <div className="navigation-buttons">
        <button 
          className="btn-secondary" 
          onClick={() => {
            // If user clicked "No" and there's double insurance, go back to Cover Details to change start date
            if (doubleInsuranceCheck.hasDoubleInsurance && !useNextAvailableDate && doubleInsuranceCheck.nextAvailableDate && onBackToCoverDetails) {
              onBackToCoverDetails();
            } else {
              onBack();
            }
          }}
        >
          {doubleInsuranceCheck.hasDoubleInsurance && !useNextAvailableDate && doubleInsuranceCheck.nextAvailableDate 
            ? '← Back to Cover Details (Change Start Date)' 
            : '← Back to Insured Details'}
        </button>
        <button
          id="continueButton"
          className={doubleInsuranceCheck.hasDoubleInsurance && !doubleInsuranceCheck.canProceed ? 'btn-primary error' : 'btn-primary'}
          onClick={handleContinue}
          disabled={
            !isFormComplete() ||
            isCheckingInsurance ||
            (doubleInsuranceCheck.hasDoubleInsurance &&
             !doubleInsuranceCheck.canProceed &&
             doubleInsuranceCheck.nextAvailableDate &&
             !useNextAvailableDate &&
             (() => {
               const chosenNorm = coverStartRef.current || toYYYYMMDD(startDate) || toYYYYMMDD(effectiveStartDate);
               const nextNorm = toYYYYMMDD(doubleInsuranceCheck.nextAvailableDate) || doubleInsuranceCheck.nextAvailableDate;
               return nextNorm && (!chosenNorm || chosenNorm < nextNorm);
             })())
          }
        >
          {isCheckingInsurance ? '🔍 Checking Insurance...' : 
           (() => {
             if (doubleInsuranceCheck.canProceed) return 'Continue to Checkout →';
             const chosenNorm = coverStartRef.current || toYYYYMMDD(startDate) || toYYYYMMDD(effectiveStartDate);
             const nextNorm = toYYYYMMDD(doubleInsuranceCheck.nextAvailableDate) || doubleInsuranceCheck.nextAvailableDate;
             const mustConfirm = doubleInsuranceCheck.hasDoubleInsurance && !doubleInsuranceCheck.canProceed && nextNorm && !useNextAvailableDate && (!chosenNorm || chosenNorm < nextNorm);
             return mustConfirm ? '⚠️ Please confirm date selection above' :
               doubleInsuranceCheck.hasDoubleInsurance && !doubleInsuranceCheck.canProceed ? '❌ Cannot Proceed - Double Insurance' :
               'Continue to Checkout →';
           })()}
        </button>
      </div>

      {/* Snackbar Notifications */}
      {showSnackbar && (
        <Snackbar
          message={snackbarMessage}
          type={snackbarType}
          onClose={() => setShowSnackbar(false)}
          duration={snackbarType === 'error' ? 0 : 5000}
        />
      )}
    </div>
  );
};

export default VehicleDetails;
